// src/pages/QuestionPage.jsx
import React, { useState, useEffect, useRef } from "react";
import { skipToken } from "@reduxjs/toolkit/query";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  Form,
  Button,
  Progress,
  Spin,
  Checkbox,
  notification,
  Collapse,
} from "antd";
import {
  useCreateResponseMutation,
  useCreateUserResponseMutation,
  useGetFormDetailsQuery,
  useGetFormQuestionsQuery,
  useUploadFileMutation,
  useCheckPanelUserSurveyAccessQuery,
} from "../../store/api/apiSlice";
import { SurveyHeader } from "../../components/survey/SurveyHeader";
import CustomVideoPlayer from "../../components/common/CustomVideoPlayer";
import DynamicInput from "../../components/survey/DynamicInput";
import { motion, AnimatePresence } from "framer-motion";
import { AnanymousIcon, OrganizationsIcon } from "../../assets/svgs/icons";
import { RightOutlined } from "@ant-design/icons";
import { buildSurveyUserInfo } from "../../utils/surveyContact";
import {
  appendSurveyTrackingParam,
  getPanelSurveyAccessBlock,
  getSurveyTrackingToken,
  persistSurveyResponseId,
  persistSurveyTrackingToken,
  withSurveyQueryString,
} from "../../utils/surveyTracking";
import {
  findConditionalNextQuestion,
  getVisibleInputEntries,
  isCurrentQuestionConditionSource,
  isInputConditionMet,
} from "../../utils/inputConditions";
import {
  applyPayoutFromApiResult,
  clearSurveyPayoutState,
  showPayoutSnackbar,
} from "../../utils/surveyPayout";
import {
  getUniqueSections,
  normalizeSectionLabel,
  SurveySectionLabels,
} from "../../components/survey/SurveySectionLabels";
import {
  getFormOrganizationLogoUrl,
  pickMediaUrl,
} from "../../utils/formTabs";
import { isRadioListInputType, isRadioListRequiredEmpty } from "../../utils/radioList";
import { getQuestionTitleNumber } from "../../utils/questionNumber";

const resolveOrgHeaderImageUrl = (organization, formRoot) =>
  getFormOrganizationLogoUrl(formRoot) ||
  pickMediaUrl(organization?.logo) ||
  pickMediaUrl(organization?.image) ||
  pickMediaUrl(formRoot?.image) ||
  null;

const QuestionPage = () => {
  const { formId, questionNumber } = useParams();
  const [searchParams] = useSearchParams();
  const trackingToken = getSurveyTrackingToken(formId, searchParams);
  const enquiryType = searchParams.get("type");
  const withQuery = (path) => withSurveyQueryString(path, searchParams);
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const watchedResponses = Form.useWatch("responses", form) || [];
  const [createResponse] = useCreateResponseMutation();
  const [createUserResponse] = useCreateUserResponseMutation();
  const [uploadFile] = useUploadFileMutation();

  const [responses, setResponses] = useState(
    JSON.parse(localStorage.getItem(`survey_${formId}_responses`) || "{}"),
  );
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [orgLogoError, setOrgLogoError] = useState(false);
  const [direction, setDirection] = useState(
    localStorage.getItem("surveyAnimationDirection") || "vertical",
  );
  const accessSnackbarShownRef = useRef(false);

  const { data: formData, isLoading: formLoading } =
    useGetFormDetailsQuery(formId);
  const { data: questionsData, isLoading: questionsLoading } =
    useGetFormQuestionsQuery(formId);
  const { data: surveyAccessData } =
    useCheckPanelUserSurveyAccessQuery(trackingToken ?? skipToken);
  const organizationTermsRoute = `/organization-terms-and-conditions?formId=${encodeURIComponent(
    formId,
  )}`;
  const organizationPrivacyRoute = `/organization-privacy-policy?formId=${encodeURIComponent(
    formId,
  )}`;
  const sortedQuestions = [...(questionsData?.data || [])].sort(
    (a, b) => a.number - b.number,
  );
  const orgLogoUrl = resolveOrgHeaderImageUrl(
    formData?.data?.organization,
    formData?.data,
  );

  const questions = sortedQuestions || [];
  const currentQuestion = questions[currentQuestionIndex];
  const questionTitleNumber = getQuestionTitleNumber(
    currentQuestion,
    currentQuestionIndex,
  );
  const totalQuestions = questions.length;
  const sectionLabels = getUniqueSections(questions);
  const activeSection = normalizeSectionLabel(currentQuestion?.section);

  const uploadedRecordVideos = (currentQuestion?.response || [])
    .map((input, idx) => ({
      inputId: input?._id || `record-video-${idx}`,
      isRecordVideo: input?.input_type === "record-video",
      value: watchedResponses?.[idx],
    }))
    .filter(
      (item) =>
        item.isRecordVideo &&
        typeof item.value === "string" &&
        item.value.trim().length > 0,
    );

  const hasUploadedRecordVideo = uploadedRecordVideos.length > 0;

  useEffect(() => {
    if (!currentQuestion?.response?.length) return;

    currentQuestion.response.forEach((input, index) => {
      if (isInputConditionMet(input, watchedResponses)) return;

      const current = form.getFieldValue(["responses", index]);

      if (current === undefined || current === null || current === "") return;

      if (Array.isArray(current) && current.length === 0) return;

      form.setFieldValue(["responses", index], undefined);
    });
  }, [watchedResponses, currentQuestion, form]);

  useEffect(() => {
    persistSurveyTrackingToken(formId, searchParams);
  }, [formId, searchParams]);

  useEffect(() => {
    if (!trackingToken || !surveyAccessData) return;

    const snackbar =
      surveyAccessData?.snackbar || surveyAccessData?.payout?.snackbar;

    if (snackbar?.message && !accessSnackbarShownRef.current) {
      accessSnackbarShownRef.current = true;
      showPayoutSnackbar(snackbar, {
        canRetry: surveyAccessData?.can_retry_payout === true,
      });
    }

    const accessBlock = getPanelSurveyAccessBlock(surveyAccessData);

    if (accessBlock) {
      navigate(withQuery(`/form-submissions/panel-error-page/${formId}`), {
        replace: true,
        state: {
          reason: accessBlock,
          canRetryPayout: surveyAccessData?.can_retry_payout === true,
          payout: surveyAccessData?.payout || {
            canRetry: surveyAccessData?.can_retry_payout === true,
            payoutSent: false,
            payoutError: surveyAccessData?.payout_error || null,
            snackbar,
          },
        },
      });
    }
  }, [
    trackingToken,
    surveyAccessData,
    navigate,
    formId,
    searchParams,
  ]);

  const withTrackingParam = (payload) =>
    appendSurveyTrackingParam(payload, formId, searchParams);

  const handleAnonymousChange = (e) => {
    if (!formData?.data?.anonymous) {
      setIsAnonymous(e.target.checked);
    }
  };

  const handleDirectionChange = (newDirection) => {
    setDirection(newDirection);
    localStorage.setItem("surveyAnimationDirection", newDirection);
  };

  const handleSubmit = async (values) => {
    try {
      if (formData.data.anonymous) {
        values.anonymous = true;
      }

      if (!values.responses) {
        values.responses = [];
      }

      let hasEmptyRequiredFields = false;

      currentQuestion?.response?.forEach((input, index) => {
        if (!isInputConditionMet(input, values.responses)) {
          return;
        }

        const response = values.responses?.[index];

        if (input.require !== false) {
          if (
            !response ||
            (input.input_type === "checkbox" && response.length === 0) ||
            (input.input_type === "slider" && !response) ||
            (input.input_type === "radio" && !response) ||
            (input.input_type === "record-video" && !response) ||
            (input.input_type === "file" && !response) ||
            (input.input_type === "multiple-files" &&
              (!response ||
                (Array.isArray(response) && response.length === 0))) ||
            isRadioListRequiredEmpty(input, response)
          ) {
            form.setFields([
              {
                name: ["responses", index.toString()],
                errors: [
                  `Please ${
                    input.input_type === "record-video"
                      ? "record a video before proceeding"
                      : input.input_type === "file" ||
                          input.input_type === "multiple-files"
                        ? "upload a file"
                        : input.input_type === "slider"
                          ? "select a rating"
                          : input.input_type === "checkbox"
                            ? "select at least one option"
                            : input.input_type === "radio"
                              ? "select an option"
                              : isRadioListInputType(input.input_type)
                                ? "select an option for every row"
                                : "provide a response"
                  }`,
                ],
              },
            ]);

            hasEmptyRequiredFields = true;
          }
        }
      });

      if (hasEmptyRequiredFields) {
        return;
      }

      setIsSubmitting(true);

      let formattedResponses = {};
      let fileUploadPromises = [];
      let immediateUploadFiles = [];

      currentQuestion?.response?.forEach((input, index) => {
        if (!isInputConditionMet(input, values.responses)) {
          return;
        }

        const response = values.responses?.[index];

        if (response) {
          const responseKey =
            input.input_title || `question_${currentQuestion._id}_${index}`;

          if (input.input_type === "record-video") {
            if (typeof response === "string" && response.startsWith("http")) {
              formattedResponses[responseKey] = {
                type: "record-video",
                value: response,
                inputId: input._id,
              };
            } else if (response instanceof Blob || response instanceof File) {
              formattedResponses[responseKey] = {
                type: "record-video",
                value: "uploading_" + Date.now(),
                inputId: input._id,
              };

              const uploadPromise = (async () => {
                try {
                  const uploadResponse = await uploadFile({
                    file: response,
                    filename: response?.name || `video-${Date.now()}.webm`,
                  }).unwrap();

                  if (uploadResponse?.file) {
                    const updatePayload = withTrackingParam({
                      form: formId,
                      question: currentQuestion._id,
                      response: JSON.stringify({
                        responses: {
                          ...formattedResponses,
                          [responseKey]: {
                            type: "record-video",
                            value: uploadResponse.file,
                            inputId: input._id,
                          },
                        },
                        questionId: currentQuestion._id,
                        step: currentQuestionIndex + 1,
                      }),
                      step: Number(currentQuestionIndex + 1),
                      total_questions: Number(totalQuestions),
                      user: localStorage.getItem("SurevyUserId"),
                    });

                    await createResponse(updatePayload);
                  }
                } catch (error) {
                  console.error("Background video upload failed:", error);
                }
              })();

              fileUploadPromises.push(uploadPromise);
            }
          } else if (
            (input.input_type === "file" ||
              input.input_type === "multiple-files") &&
            response
          ) {
            const isMultiple = input.input_type === "multiple-files";
            const items = Array.isArray(response) ? response : [response];
            const files = items.filter(
              (item) => item instanceof Blob || item instanceof File,
            );
            const urls = items.filter(
              (item) =>
                typeof item === "string" && item.trim().startsWith("http"),
            );

            if (isMultiple) {
              formattedResponses[responseKey] = {
                type: "multiple-files",
                value: urls,
                inputId: input._id,
              };

              files.forEach((file) => {
                immediateUploadFiles.push({
                  file,
                  responseKey,
                  isMultiple: true,
                });
              });
            } else if (files[0]) {
              formattedResponses[responseKey] = {
                type: "file",
                value: "uploading_" + Date.now(),
                inputId: input._id,
              };

              immediateUploadFiles.push({
                file: files[0],
                responseKey,
                isMultiple: false,
              });
            } else if (urls[0]) {
              formattedResponses[responseKey] = {
                type: "file",
                value: urls[0],
                inputId: input._id,
              };
            }
          } else if (input.input_type === "checkbox") {
            formattedResponses[responseKey] = {
              type: "checkbox",
              value: Array.isArray(response) ? response : [response],
              inputId: input._id,
            };
          } else {
            formattedResponses[responseKey] = {
              type: input.input_type,
              value: response,
              inputId: input._id,
            };
          }
        }
      });

      if (immediateUploadFiles.length > 0) {
        try {
          for (const item of immediateUploadFiles) {
            const uploadResponse = await uploadFile({
              file: item.file,
              filename: item.file?.name || `file-${Date.now()}`,
            }).unwrap();

            if (uploadResponse?.file) {
              if (item.isMultiple) {
                const current = formattedResponses[item.responseKey];
                const prev = Array.isArray(current?.value) ? current.value : [];

                formattedResponses[item.responseKey] = {
                  ...current,
                  type: "multiple-files",
                  value: [...prev, uploadResponse.file],
                };
              } else {
                formattedResponses[item.responseKey] = {
                  type: "file",
                  value: uploadResponse.file,
                  inputId: formattedResponses[item.responseKey].inputId,
                };
              }
            }
          }
        } catch (error) {
          notification.error({
            message: "Upload Error",
            description: "Failed to upload file. Please try again.",
          });

          setIsSubmitting(false);

          return;
        }
      }

      const payload = {
        responses: formattedResponses,
        questionId: currentQuestion._id,
        step: currentQuestion.number,
      };

      let submitApiResult = null;

      if (currentQuestionIndex === 0) {
        const contact = buildSurveyUserInfo(currentQuestion, values);
        const userInfo = {
          full_name: contact.full_name || "",
          email: contact.email || "",
        };

        const createuser = await createUserResponse(userInfo).unwrap();
        localStorage.setItem("SurevyUserId", createuser.data._id);

        const createPayload = withTrackingParam({
          user: createuser.data._id,
          form: formId,
          question: currentQuestion._id,
          response: JSON.stringify({
            ...payload,
            anonymous: values.anonymous || false,
            terms: values.terms || false,
          }),
          step: Number(currentQuestion.number),
          total_questions: Number(totalQuestions),
        });

        const response = await createResponse(createPayload).unwrap();

        if (response?.data?._id) {
          persistSurveyResponseId(formId, response.data._id);
        }

        submitApiResult = response;
      } else {
        const updatePayload = withTrackingParam({
          form: formId,
          question: currentQuestion._id,
          response: JSON.stringify(payload),
          step: Number(currentQuestion.number),
          total_questions: Number(totalQuestions),
          user: localStorage.getItem("SurevyUserId"),
        });

        submitApiResult = await createResponse(updatePayload).unwrap();
      }

      const updatedResponses = {
        ...responses,
        [currentQuestion._id]: formattedResponses,
      };

      setResponses(updatedResponses);

      localStorage.setItem(
        `survey_${formId}_responses`,
        JSON.stringify(updatedResponses),
      );

      const conditionalNextQuestion = findConditionalNextQuestion(
        questions,
        currentQuestion,
        values.responses,
      );

      const navigateToThankYou = () => {
        const { payout } = applyPayoutFromApiResult(formId, submitApiResult);

        if (payout?.payoutSent) {
          clearSurveyPayoutState(formId);
        }

        navigate(withQuery(`/form-submissions/thank-you/${formId}`), {
          state: {
            payout,
            canRetryPayout: Boolean(payout?.canRetry),
          },
        });
      };

      if (conditionalNextQuestion) {
        navigate(
          withQuery(
            `/form-submissions/question/${formId}/${conditionalNextQuestion.number}`,
          ),
        );
      } else if (currentQuestionIndex < totalQuestions - 1) {
        const isConditionalQuestion = isCurrentQuestionConditionSource(
          questions,
          currentQuestion,
        );

        if (isConditionalQuestion) {
          const currentNumber = currentQuestion.number;
          const remainingQuestions = questions
            .filter((q) => q.number > currentNumber)
            .sort((a, b) => a.number - b.number);

          if (remainingQuestions.length > 0) {
            navigate(
              withQuery(
                `/form-submissions/question/${formId}/${remainingQuestions[0].number}`,
              ),
            );
          } else {
            navigateToThankYou();
          }
        } else {
          const nextQuestion = questions[currentQuestionIndex + 1];

          navigate(
            withQuery(
              `/form-submissions/question/${formId}/${nextQuestion.number}`,
            ),
          );
        }
      } else {
        navigateToThankYou();
      }

      setIsSubmitting(false);
    } catch (error) {
      setIsSubmitting(false);

      const apiMessage =
        error?.data?.message ||
        error?.error?.data?.message ||
        error?.data?.payout?.payoutError ||
        error?.message ||
        "Failed to save response";

      notification.error({
        message: "Error",
        description: String(apiMessage),
        duration: 10,
        placement: "topRight",
        style: { whiteSpace: "pre-wrap", maxWidth: 440 },
      });
    }
  };

  const getAnimationProps = () => {
    const dir = localStorage.getItem("surveyAnimationDirection") || "vertical";

    if (dir === "vertical") {
      return {
        initial: { y: 40, opacity: 0 },
        animate: { y: 0, opacity: 1 },
        exit: { y: -40, opacity: 0 },
        transition: { duration: 0.3 },
      };
    } else {
      return {
        initial: { x: 40, opacity: 0 },
        animate: { x: 0, opacity: 1 },
        exit: { x: -40, opacity: 0 },
        transition: { duration: 0.3 },
      };
    }
  };

  const getNavigationButtonText = () => {
    const isLastQuestion = currentQuestionIndex === totalQuestions - 1;
    const hasMultipleQuestions = totalQuestions > 1;
    return currentQuestion?.buttonType === "start" && !hasUploadedRecordVideo
      ? "Start"
      : currentQuestion?.buttonType === "next"
        ? "Next"
        : currentQuestion?.buttonType === "finish"
          ? "Submit"
          : isLastQuestion
            ? "Submit"
            : hasMultipleQuestions
              ? "Next"
              : "Next";
  };

  const BottomNavigation = () => {
    if (direction !== "vertical" && !currentQuestion?.showButton) {
      return null;
    }

    const buttonText = getNavigationButtonText();
    const shouldHideSubmitButton =
      formData?.data?.status !== "active" && buttonText === "Submit";
    if (shouldHideSubmitButton) return null;

    return (
      <div className="mt-4 flex w-full justify-center">
        <Button
          type="primary"
          loading={isSubmitting}
          htmlType="submit"
          className="cursor-pointer shadow-lg flex items-center justify-center px-8 py-2 rounded-2xl w-full"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {buttonText}
        </Button>
      </div>
    );
  };

  useEffect(() => {
    if (formData?.data?.anonymous) {
      setIsAnonymous(true);
    }
  }, [formData]);

  useEffect(() => {
    setOrgLogoError(false);
  }, [formData?.data?.organization?.logo]);

  useEffect(() => {
    if (!localStorage.getItem("surveyAnimationDirection")) {
      localStorage.setItem("surveyAnimationDirection", "vertical");

      setDirection("vertical");
    }
  }, []);

  useEffect(() => {
    if (questions) {
      const newIndex = questions.findIndex(
        (q) => q.number?.toString() === questionNumber,
      );

      if (newIndex !== -1) {
        setCurrentQuestionIndex(newIndex);
      } else {
        setCurrentQuestionIndex(0);
      }
    }
  }, [questionNumber, questionsData]);

  useEffect(() => {
    form.resetFields();

    if (currentQuestion && responses[currentQuestion._id]) {
      try {
        if (currentQuestionIndex === 0) {
          const savedData = JSON.parse(
            localStorage.getItem(`survey_${formId}_responses`) || "{}",
          );
          const firstQuestionData = savedData[currentQuestion._id];

          if (firstQuestionData) {
            form.setFieldsValue({
              anonymous: firstQuestionData.anonymous || false,
              terms: false,
            });
          }
        }
      } catch (error) {
        form.resetFields();
      }
    }
  }, [currentQuestion, responses, currentQuestionIndex, formId]);

  if (formLoading || questionsLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={currentQuestion?._id || "loading"}
        {...getAnimationProps()}
        className="box-border flex min-h-0 min-w-0 max-w-full flex-1 flex-col overflow-x-hidden overflow-y-hidden bg-white p-3 sm:p-4 md:p-2"
      >
        <div className="mx-auto flex min-h-0 w-full min-w-0 max-w-5xl flex-1 flex-col rounded-2xl border border-gray-200 bg-[#F5F5F5]">
          {enquiryType !== "enquiry" ? (
            <>
              <div className="hidden shrink-0 md:block">
                <SurveyHeader
                  onDirectionChange={handleDirectionChange}
                  organization={formData?.data?.organization}
                  form={formData?.data}
                  onCancel={() => navigate("/")}
                />
                <div className="p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <h1 className="mb-1 mt-0 text-xl font-bold text-primary">
                        {formData?.data?.title}
                      </h1>
                      {formData?.data?.description ? (
                        <p className="mb-3 mt-0 text-sm leading-relaxed text-gray-500">
                          {formData?.data?.description}
                        </p>
                      ) : null}
                    </div>
                    {isAnonymous ? (
                      <div className="flex shrink-0 items-center gap-2 rounded-full bg-gray-50 px-3 py-1">
                        <span className="text-sm text-gray-700">
                          <span className="relative top-1">
                            <AnanymousIcon />
                          </span>{" "}
                          Anonymous Response
                        </span>
                      </div>
                    ) : null}
                  </div>
                  <Progress
                    percent={
                      ((currentQuestionIndex + 1) / totalQuestions) * 100
                    }
                    showInfo={false}
                    strokeColor="#202835"
                    className="mb-0"
                  />
                  <SurveySectionLabels
                    sections={sectionLabels}
                    activeSection={activeSection}
                  />
                </div>
              </div>

              <div className="mb-3 shrink-0 md:hidden">
                <Collapse
                  ghost
                  expandIconPosition="end"
                  className="rounded-2xl border border-gray-200 bg-white [&_.ant-collapse-header]:!flex-nowrap [&_.ant-collapse-header]:items-center [&_.ant-collapse-header]:gap-2 [&_.ant-collapse-expand-icon]:shrink-0"
                  style={{
                    background:
                      formData?.data?.organization?.color || undefined,
                  }}
                  expandIcon={({ isActive }) => (
                    <RightOutlined
                      rotate={isActive ? 90 : 0}
                      className="text-gray-500"
                    />
                  )}
                  items={[
                    {
                      key: "1",
                      label: (
                        <div className="flex w-full items-center justify-between gap-2">
                          <div className="flex min-w-0 flex-nowrap items-center gap-2">
                            {orgLogoUrl && !orgLogoError ? (
                              <img
                                src={orgLogoUrl}
                                alt={formData?.data?.organization?.name}
                                className="h-8 w-8 shrink-0 rounded-full border border-gray-200 object-cover"
                                onError={() => setOrgLogoError(true)}
                              />
                            ) : (
                              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/60">
                                <OrganizationsIcon />
                              </div>
                            )}
                            <div className="min-w-0">
                              <span className="block truncate font-medium">
                                {formData?.data?.organization?.name}
                              </span>
                              {/* {(formData?.data?.organization?.location?.name ||
                                formData?.data?.organization?.address) && (
                                <span className="block truncate text-xs text-gray-500">
                                  {formData?.data?.organization?.location
                                    ?.name ||
                                    formData?.data?.organization?.address}
                                </span>
                              )} */}
                            </div>
                          </div>
                          {isAnonymous ? (
                            <div className="flex shrink-0 items-center gap-2 rounded-full bg-gray-50 px-3 py-1">
                              <span className="text-sm">
                                <span className="relative top-1">
                                  <AnanymousIcon />
                                </span>{" "}
                                Anonymous
                              </span>
                            </div>
                          ) : null}
                        </div>
                      ),
                      children: (
                        <div className="space-y-3 pt-2">
                          <h1 className="mb-0 mt-0 text-lg font-bold text-primary">
                            {formData?.data?.title}
                          </h1>
                          {formData?.data?.description ? (
                            <p className="mb-0 text-sm text-gray-500">
                              {formData?.data?.description}
                            </p>
                          ) : null}
                          <Progress
                            percent={
                              ((currentQuestionIndex + 1) / totalQuestions) *
                              100
                            }
                            showInfo={false}
                            strokeColor="#202835"
                            className="m-0 mb-0"
                          />
                          <SurveySectionLabels
                            sections={sectionLabels}
                            activeSection={activeSection}
                          />
                        </div>
                      ),
                    },
                  ]}
                />
              </div>
            </>
          ) : null}

          <Form
            form={form}
            onFinish={handleSubmit}
            initialValues={{
              ...responses[currentQuestion?._id],
              terms: false,
            }}
            layout="vertical"
            className="flex min-h-0 w-full min-w-0 flex-1 flex-col"
          >
            <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto">
              <div
                className={`box-border grid w-full min-w-0 grid-cols-1 items-start gap-4 overflow-x-hidden border-t border-gray-200 p-2 md:p-4 ${
                  currentQuestion?.video || hasUploadedRecordVideo
                    ? "md:grid-cols-[minmax(0,340px)_minmax(0,1fr)]"
                    : "md:grid-cols-1"
                }`}
              >
                {(currentQuestion?.video || hasUploadedRecordVideo) && (
                  <div className="hidden min-w-0 md:block">
                    <div className="w-full overflow-hidden rounded-3xl border border-gray-200 bg-white">
                      <CustomVideoPlayer
                        autoPlay
                        loop
                        preload="metadata"
                        src={currentQuestion?.video || ""}
                        videoUploaded={hasUploadedRecordVideo}
                        playsInline
                        wrapperClassName="rounded-none"
                      />
                    </div>
                  </div>
                )}

                <div className="min-w-0 max-w-full">
                  <div className="mb-3 rounded-3xl border border-gray-200 bg-white p-4 md:p-5">
                    <h3 className="mb-0 mt-0 flex flex-row items-start gap-2 font-bold text-gray-900">
                      {questionTitleNumber != null ? (
                        <span className="shrink-0 text-base tabular-nums leading-snug sm:text-lg">
                          {questionTitleNumber})
                        </span>
                      ) : null}
                      <span className="min-w-0 flex-1 break-words text-lg leading-snug sm:text-xl md:text-lg">
                        {currentQuestion?.title}
                      </span>
                    </h3>
                    {currentQuestion?.description ? (
                      <p className="mb-0 mt-2 text-sm text-gray-500">
                        {currentQuestion?.description}
                      </p>
                    ) : null}
                  </div>

                  <div className="mb-1 rounded-3xl border border-gray-200 bg-white p-3 md:p-5">
                    {(currentQuestion?.video || hasUploadedRecordVideo) && (
                      <div className="mb-3 w-full overflow-hidden rounded-3xl border border-gray-200 bg-white md:hidden">
                        <CustomVideoPlayer
                          autoPlay
                          loop
                          preload="metadata"
                          src={currentQuestion?.video || ""}
                          videoUploaded={hasUploadedRecordVideo}
                          playsInline
                          wrapperClassName="rounded-none"
                        />
                      </div>
                    )}

                    <div className="grid min-w-0 grid-cols-1 gap-3">
                      {getVisibleInputEntries(
                        currentQuestion?.response,
                        watchedResponses,
                      ).map(({ input, index }) => (
                        <DynamicInput
                          key={input._id || `${input.input_title}-${index}`}
                          input={input}
                          form={form}
                          index={index}
                        />
                      ))}
                    </div>

                    {currentQuestionIndex === 0 && (
                      <div className="mt-3 space-y-1">
                        {!formData?.data?.anonymous &&
                          currentQuestion?.showAnonymous && (
                            <Form.Item
                              className="mb-0"
                              name="anonymous"
                              valuePropName="checked"
                            >
                              <Checkbox onChange={handleAnonymousChange}>
                                Make My Response{" "}
                                <span>
                                  Anonymous
                                  <span className="relative top-1">
                                    <AnanymousIcon />
                                  </span>
                                </span>
                              </Checkbox>
                            </Form.Item>
                          )}
                        {currentQuestion?.showTerms && (
                          <Form.Item
                            className="mb-0"
                            name="terms"
                            valuePropName="checked"
                            rules={[
                              {
                                validator: async (_, value) => {
                                  if (!value) {
                                    throw new Error(
                                      "Please accept the terms & conditions",
                                    );
                                  }
                                },
                              },
                            ]}
                            required
                          >
                            <Checkbox>
                              Accept our{" "}
                              <a
                                target="terms-and-conditions"
                                href={organizationTermsRoute}
                                className="cursor-pointer underline"
                              >
                                Terms & Conditions
                              </a>{" "}
                              and{" "}
                              <a
                                target="_blank"
                                href={organizationPrivacyRoute}
                                className="cursor-pointer underline"
                                rel="noreferrer"
                              >
                                Privacy Policy
                              </a>
                            </Checkbox>
                          </Form.Item>
                        )}
                      </div>
                    )}

                    {direction === "vertical" && <BottomNavigation />}
                  </div>
                </div>
              </div>
            </div>

            {direction !== "vertical" &&
              !(
                formData?.data?.status !== "active" &&
                getNavigationButtonText() === "Submit"
              ) && (
                <div className="mt-auto flex shrink-0 justify-end border-t border-gray-100 bg-white py-4 px-4">
                  <Button
                    type="primary"
                    loading={isSubmitting}
                    htmlType="submit"
                    className="flex w-full cursor-pointer items-center justify-center rounded-2xl px-8 py-2 shadow-lg sm:w-auto"
                  >
                    {getNavigationButtonText()}
                  </Button>
                </div>
              )}
          </Form>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default QuestionPage;
